import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatNaira } from "@/lib/utils";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";

// Placeholder payment type until payment system is implemented
export interface Payment {
    id: string;
    amount: number;
    date: string;
    status: "paid" | "pending" | "overdue" | "failed";
    method: string;
    reference?: string;
    description?: string;
}

interface PaymentHistoryTableProps {
    payments: Payment[];
    showDownload?: boolean;
    emptyMessage?: string;
}

export function PaymentHistoryTable({
    payments,
    showDownload = true,
    emptyMessage = "No payment history available",
}: PaymentHistoryTableProps) {
    const getStatusBadge = (status: Payment["status"]) => {
        switch (status) {
            case "paid":
                return <Badge className="bg-green-500">Paid</Badge>;
            case "pending":
                return <Badge className="bg-yellow-500">Pending</Badge>;
            case "overdue":
                return <Badge variant="destructive">Overdue</Badge>;
            case "failed":
                return <Badge variant="outline" className="border-red-300 text-red-800">Failed</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-NG", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>Payment History</CardTitle>
                <CardDescription>
                    View your rent payment history and download receipts
                </CardDescription>
            </CardHeader>
            <CardContent>
                {payments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                        {emptyMessage}
                    </div>
                ) : (
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Method</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Status</TableHead>
                                    {showDownload && <TableHead className="text-right">Receipt</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {payments.map((payment) => (
                                    <TableRow key={payment.id}>
                                        <TableCell className="font-medium">
                                            {formatDate(payment.date)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span>{payment.description || "Rent Payment"}</span>
                                                {payment.reference && (
                                                    <span className="text-xs text-muted-foreground">
                                                        Ref: {payment.reference}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="capitalize">{payment.method}</TableCell>
                                        <TableCell className="font-semibold">
                                            {formatNaira(payment.amount)}
                                        </TableCell>
                                        <TableCell>{getStatusBadge(payment.status)}</TableCell>
                                        {showDownload && (
                                            <TableCell className="text-right">
                                                {payment.status === "paid" && (
                                                    <Button variant="ghost" size="sm">
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
